import { cn } from '@/lib/utils';

const R = 6;
const CIRCUMFERENCE = 2 * Math.PI * R;

/**
 * How full this chat's working memory is (server `context_pct`). Quiet on
 * purpose: a thin ring that only takes colour near the point where Baxter
 * starts compacting older steps.
 */
export function ContextMeter({ pct }: { pct: number }) {
  const value = Math.max(0, Math.min(100, Math.round(pct)));
  const label =
    value >= 100
      ? 'Context full — Baxter is summarising older steps. Start a new chat for a fresh start.'
      : `Context ${value}% used. Past 100% Baxter summarises older steps.`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid="baxter-context-meter"
      className={cn(
        'flex h-7 min-w-7 items-center justify-center gap-0.5 px-0.5',
        value >= 100 ? 'text-destructive' : value >= 80 ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground/70',
      )}
    >
      <svg viewBox="0 0 16 16" className="size-3.5 -rotate-90" aria-hidden="true">
        <circle cx="8" cy="8" r={R} fill="none" stroke="currentColor" strokeOpacity={0.25} strokeWidth="2" />
        {value > 0 && <circle
          cx="8"
          cy="8"
          r={R}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - value / 100)}
          className="transition-[stroke-dashoffset] duration-(--duration-step) ease-out motion-reduce:transition-none"
        />}
      </svg>
      {/* Quiet until it matters; then say the number (a title never shows on touch). */}
      {value >= 80 && <span className="text-[11px] tabular-nums">{value}%</span>}
    </span>
  );
}
